/**
 * Eligibility status display — single source of truth for every
 * eligibility badge in the app (client list, chart header,
 * eligibility tab table, calendar cells, appointment cards,
 * insurance dialog, client portal).
 *
 * Why this exists: before this file, eligibility status rendering was
 * duplicated across ~6 components with slightly different icons,
 * colors, and labels.  Users reported confusion:
 *  - Unicode checkmarks vs lucide icons rendering differently across
 *    browsers/devices
 *  - Inconsistent label text ("Eligible" vs "Verified" vs "Active")
 *  - Green/yellow/red shades not matching between surfaces
 *
 * Rules enforced here:
 *  - ONE icon per status (lucide-react — crisp + identical everywhere)
 *  - ONE label per status
 *  - ONE color scheme (tailwind classes) per status
 *  - `verified` and `active` are aliases for the same thing (payer API
 *    returns `active`; our client doc stores `verified` after auto-sync)
 *  - `pending` covers `submitted`, `queued`, `received`, `processing`
 *    — all mean "waiting on 271"
 *  - `inactive` = payer confirmed coverage is NOT active
 *  - `rejected` = the 270 bounced (bad member ID, name mismatch, etc.)
 *  - `pre_coverage` / `post_coverage` = payer confirmed coverage for a
 *    DIFFERENT date range than the appointment's DOS (DOS-aware render)
 */

import React from 'react';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Circle,
} from 'lucide-react';

// ── Status dictionary ─────────────────────────────────────────────
// Tailwind classes chosen so the color reads the same in both light
// backgrounds (table rows) and colored backgrounds (status badges).
export const ELIGIBILITY_DISPLAY = {
  verified: {
    IconComponent: CheckCircle2,
    iconClass: 'text-green-600',
    color: 'text-green-700',
    bg: 'bg-green-100',
    border: 'border-green-300',
    label: 'Eligible',
  },
  active: {
    IconComponent: CheckCircle2,
    iconClass: 'text-green-600',
    color: 'text-green-700',
    bg: 'bg-green-100',
    border: 'border-green-300',
    label: 'Eligible',
  },
  pending: {
    IconComponent: Clock,
    iconClass: 'text-amber-600',
    color: 'text-amber-800',
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    label: 'Pending',
  },
  submitted: {
    IconComponent: Clock,
    iconClass: 'text-amber-600',
    color: 'text-amber-800',
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    label: 'Pending',
  },
  queued: {
    IconComponent: Clock,
    iconClass: 'text-amber-600',
    color: 'text-amber-800',
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    label: 'Pending',
  },
  received: {
    IconComponent: Clock,
    iconClass: 'text-amber-600',
    color: 'text-amber-800',
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    label: 'Pending',
  },
  processing: {
    IconComponent: Clock,
    iconClass: 'text-amber-600',
    color: 'text-amber-800',
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    label: 'Pending',
  },
  inactive: {
    IconComponent: XCircle,
    iconClass: 'text-red-600',
    color: 'text-red-700',
    bg: 'bg-red-100',
    border: 'border-red-300',
    label: 'Not Eligible',
  },
  rejected: {
    IconComponent: XCircle,
    iconClass: 'text-red-600',
    color: 'text-red-700',
    bg: 'bg-red-100',
    border: 'border-red-300',
    label: 'Rejected',
  },
  failed: {
    IconComponent: XCircle,
    iconClass: 'text-red-600',
    color: 'text-red-700',
    bg: 'bg-red-100',
    border: 'border-red-300',
    label: 'Rejected',
  },
  pre_coverage: {
    IconComponent: AlertTriangle,
    iconClass: 'text-amber-600',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    label: 'Before Coverage Start',
  },
  post_coverage: {
    IconComponent: XCircle,
    iconClass: 'text-red-600',
    color: 'text-red-700',
    bg: 'bg-red-100',
    border: 'border-red-300',
    label: 'Coverage Expired',
  },
  verified_with_discrepancies: {
    // Coverage confirmed but something mismatched (name/DOB/ID) —
    // yellow-tinged green so user knows to review before billing.
    // The biller can either sync the payer's value to the client
    // record OR explicitly dismiss the discrepancy from the
    // discrepancy review modal; once all high-severity discs are
    // resolved, the badge auto-flips back to plain "Eligible".
    IconComponent: AlertTriangle,
    iconClass: 'text-yellow-600',
    color: 'text-yellow-800',
    bg: 'bg-yellow-100',
    border: 'border-yellow-300',
    label: 'Eligible with discrepancies',
  },
  needs_review: {
    // Legacy alias of verified_with_discrepancies (Feb 2026 rename).
    // Older client docs may still carry this string; render identical
    // visuals so the UI stays consistent across the migration.
    IconComponent: AlertTriangle,
    iconClass: 'text-yellow-600',
    color: 'text-yellow-800',
    bg: 'bg-yellow-100',
    border: 'border-yellow-300',
    label: 'Eligible with discrepancies',
  },
  not_checked: {
    IconComponent: Circle,
    iconClass: 'text-gray-400',
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    label: 'Not Checked',
  },
  self_pay: {
    // Client has no insurance on file (or is configured as self-pay).
    // Renders distinctly from "not_checked" so billers know there's
    // nothing to verify here — and the row's "Check Eligibility"
    // checkbox is suppressed in the EligibilityTab so they can't
    // accidentally queue a 270 for a self-pay client (which would
    // bounce as AAA*15 / "Subscriber Not Found" anyway).
    IconComponent: Circle,
    iconClass: 'text-blue-500',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    label: 'Self Pay',
  },
  // ── DOS-aware "stale" state (Feb 2026) ─────────────────────────────
  // When the DOS is more than ``STALE_WINDOW_DAYS`` from the last 271,
  // we render a neutral grey pill instead of a green/red verdict — we
  // don't actually KNOW the coverage status on the DOS until we
  // re-verify.  Exception: a stale Inactive stays red (the biller
  // still cares about that strong signal — see rule table in PRD).
  stale_unknown: {
    IconComponent: Circle,
    iconClass: 'text-gray-400',
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    label: 'Not Verified for DOS',
  },
  // ── Backend "row" statuses on eligibility_check docs ─────────────
  // The eligibility_check.status field uses a workflow vocabulary
  // (queued → submitted → received → completed) that's separate from
  // the patient's clinical eligibility (verified / inactive / etc.).
  // History rows render the workflow status, so we need explicit map
  // entries — without them, completed checks fell through to the
  // `not_checked` default and showed the wrong "Not Checked" label
  // even though a successful 271 had landed.  Map both completion
  // states to the same green "Eligible" visuals as `verified` so the
  // History row label matches the parsed coverage_status badge.
  completed: {
    IconComponent: CheckCircle2,
    iconClass: 'text-green-600',
    color: 'text-green-700',
    bg: 'bg-green-100',
    border: 'border-green-300',
    label: 'Eligible',
  },
  completed_with_errors: {
    // Parser hit non-fatal warnings (e.g., AAA segment but valid 271
    // body, or unrecognized service-type code).  Coverage was returned
    // — render as eligible-with-discrepancies so the biller knows to
    // glance at the View 271 dialog.
    IconComponent: AlertTriangle,
    iconClass: 'text-yellow-600',
    color: 'text-yellow-800',
    bg: 'bg-yellow-100',
    border: 'border-yellow-300',
    label: 'Eligible (warnings)',
  },
};

/** Look up the display config for a status, with safe fallback.
 *  Adds a pre-rendered `icon` JSX element for backward-compat with
 *  existing call sites that do `const { icon } = getEligibilityDisplay(s); {icon}`. */
export const getEligibilityDisplay = (status) => {
  const base = ELIGIBILITY_DISPLAY[status] || ELIGIBILITY_DISPLAY['not_checked'];
  const Icon = base.IconComponent;
  return {
    ...base,
    icon: <Icon className={`${base.iconClass} inline-block`} size={14} />,
  };
};

/** Frontend mirror of `routers/appointments._client_is_self_pay`.
 *  Detects self-pay clients so the calendar / chart / EligibilityIndicator
 *  can render the blue "Self Pay" badge instead of falling through to
 *  "Not Checked" / "Unknown" — and hide actions that would queue a 270.
 *
 *  Detection (any one is enough):
 *    • client.payment_method ∈ {self_pay, cash, private_pay, ...}
 *    • client.is_self_pay / client.self_pay flag is truthy
 *    • no `insurances[]` row has BOTH a company AND a member_id, AND
 *      no top-level `primary_insurance` string is set
 *
 *  Mirror tests in /app/backend/tests/test_self_pay_detection.py.
 */
export const isClientSelfPay = (client) => {
  if (!client) return false;
  const pm = String(client.payment_method || '').trim().toLowerCase();
  if (['self_pay', 'self-pay', 'selfpay', 'cash', 'private_pay', 'private-pay'].includes(pm)) {
    return true;
  }
  if (client.is_self_pay || client.self_pay) return true;
  const insurances = Array.isArray(client.insurances) ? client.insurances : [];
  const hasRealIns = insurances.some(
    (ins) => String(ins?.company || ins?.insurance_name || '').trim()
          && String(ins?.member_id || '').trim()
  );
  if (hasRealIns) return false;
  if (String(client.primary_insurance || '').trim()) return false;
  return true;
};

/** Shorthand: just the icon component (for older inline-JSX callers). */
export const eligibilityIcon = (status, props = {}) => {
  const d = getEligibilityDisplay(status);
  const Icon = d.IconComponent;
  return <Icon className={`${d.iconClass} ${props.className || ''}`.trim()} size={props.size || 14} />;
};

/**
 * Number of days a 271 verification is considered "still trustworthy"
 * for a future or past DOS (symmetric).  Outside this window the UI
 * shifts to a neutral "Not verified for DOS — last check {date}" pill
 * because the payer didn't actually confirm coverage on the DOS.
 *
 * 7 days — set by the clinic (Feb 2026).  Most payers honor a 271
 * for the same calendar week without re-verification; longer windows
 * risk paying providers when coverage has lapsed.
 */
export const STALE_WINDOW_DAYS = 7;

/** Format YYYY-MM-DD or ISO timestamp as M/D/YYYY for the stale label. */
const _shortDate = (raw) => {
  if (!raw) return null;
  try {
    const s = String(raw);
    const hasTime = s.includes('T') || s.includes(':');
    const d = new Date(hasTime ? s : s + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  } catch {
    return null;
  }
};

/**
 * `getDOSEligibilityDisplay` — DOS-aware variant.
 *
 * Compares the appointment's date against the client's coverage
 * effective / end dates so the calendar can flag "before coverage"
 * or "after coverage" appointments distinctly from the current
 * eligibility status.
 *
 * Feb 2026 — also extracted into the shared utility so V2 calendar
 * (CalendarRoute) renders the same indicators legacy does.  Previously
 * V2 wired in a noop, so eligibility icons never showed on V2 day cells.
 *
 * Feb 2026 fix — biller report: a client whose 06/01 eligibility check
 * came back active was later flagged "Before coverage started" on the
 * 06/01 appointment because a subsequent check overwrote
 * `primary_effective_date` with a LATER plan-renewal date. Trust the
 * historical per-DOS check first: if `last_eligibility_dos` matches
 * THIS appointment's DOS and the result was verified/active, that
 * check is authoritative — don't second-guess against the cached eff
 * date.
 *
 * Feb 2026 — DOS-staleness rule (provider report: "scheduled future
 * appt, UI said Coverage Inactive — really we don't KNOW yet").
 * Symmetric ``STALE_WINDOW_DAYS`` (7) gap between DOS and last 271
 * shifts a stale verified/active to the neutral grey
 * ``stale_unknown`` pill ("Last verified active on {date} — needs
 * re-verification for DOS").  A stale Inactive keeps red because the
 * biller still wants to see that strong signal (overridable label
 * "Last verified inactive on {date}").  The result object carries an
 * optional ``labelOverride`` consumers can prefer over the default.
 */
export const getDOSEligibilityDisplay = (client, appointmentDateStr) => {
  if (!client) return getEligibilityDisplay('not_checked');
  if (isClientSelfPay(client)) return getEligibilityDisplay('self_pay');
  const status = client.primary_eligibility_status;
  const effDate = client.primary_effective_date;
  const endDate = client.primary_end_date;
  const dosStr = appointmentDateStr ? String(appointmentDateStr).substring(0, 10) : null;

  const lastCheckedDos = client.last_eligibility_dos
    ? String(client.last_eligibility_dos).substring(0, 10)
    : null;
  // Per-DOS authoritative match — if the last 271 was run FOR this
  // exact DOS and came back active, trust it regardless of staleness.
  if (
    (status === 'verified' || status === 'active')
    && dosStr && lastCheckedDos && dosStr === lastCheckedDos
  ) {
    return getEligibilityDisplay(status);
  }

  // ── Symmetric staleness window ────────────────────────────────────
  // Apply only when both a DOS and a last-check timestamp are known.
  // The window is measured from the date we VERIFIED, not from today,
  // so a 271 run yesterday-for-yesterday is still authoritative this
  // morning, and a 271 run a month ago for a DOS next week is not.
  const lastCheckedAt = client.primary_eligibility_checked
    || client.last_eligibility_check
    || client.primary_eligibility_checked_at;
  if (status && dosStr && lastCheckedAt) {
    try {
      const dosMs = new Date(dosStr + 'T00:00:00').getTime();
      const checkedMs = new Date(lastCheckedAt).getTime();
      if (Number.isFinite(dosMs) && Number.isFinite(checkedMs)) {
        const ageDays = Math.abs(dosMs - checkedMs) / 86400000;
        if (ageDays > STALE_WINDOW_DAYS) {
          const dateLabel = _shortDate(lastCheckedAt) || _shortDate(lastCheckedDos) || 'last check';
          if (status === 'inactive') {
            // Stale Inactive — keep red, but re-label so the biller
            // knows it's not a fresh confirmation for THIS DOS.
            const d = getEligibilityDisplay('inactive');
            return { ...d, labelOverride: `Last verified inactive on ${dateLabel}`, isStaleForDOS: true };
          }
          if (status === 'verified' || status === 'active') {
            // Stale Active → neutral grey, biller needs to re-verify.
            const d = getEligibilityDisplay('stale_unknown');
            return {
              ...d,
              labelOverride: `Last verified active on ${dateLabel} — re-verify for DOS`,
              isStaleForDOS: true,
            };
          }
        }
      }
    } catch {
      /* date parse failed — fall through to legacy behaviour */
    }
  }

  if ((status === 'verified' || status === 'active') && effDate && dosStr) {
    const dos = new Date(dosStr + 'T00:00:00');
    // Compare against the EARLIEST effective date we've ever seen —
    // plan renewals overwrite primary_effective_date with the new
    // period's start, which falsely flagged older covered
    // appointments as "Before Coverage Start" (Jul 2026 user report).
    const effForCompare = client.primary_earliest_effective_date || effDate;
    const eff = new Date(effForCompare + 'T00:00:00');
    const end = endDate ? new Date(endDate + 'T00:00:00') : null;
    if (!Number.isNaN(dos.getTime()) && !Number.isNaN(eff.getTime())) {
      if (dos < eff) return getEligibilityDisplay('pre_coverage');
      if (end && !Number.isNaN(end.getTime()) && dos > end) return getEligibilityDisplay('post_coverage');
    }
  }
  return getEligibilityDisplay(status);
};

/**
 * `isAppointmentSnapshotFresh` — should the calendar trust the
 * appointment-level `eligibility_verified` denormalized snapshot, or
 * should it defer to the client's freshest check?
 *
 * Mar 2026 — added because three different users (provider on cal,
 * super-admin on cal, biller in Eligibility tab) were seeing three
 * different statuses for the SAME appointment on the SAME morning,
 * all because the appointment snapshot wasn't being re-stamped when
 * a new eligibility check completed.  The new
 * `core/eligibility_propagation.py` helper fixes the write side; this
 * comparison protects the read side as belt-and-suspenders.
 *
 * Returns true when the apt snapshot's timestamp is at-least as
 * recent as the client's latest check timestamp — meaning the
 * snapshot was stamped DURING or AFTER that check, so it's
 * authoritative.  Anything stale → false → caller defers to the
 * client status (which is always updated on every check).
 */
export function isAppointmentSnapshotFresh(apt, client) {
  if (!apt || !client) return false;
  const aptIso = apt.eligibility_verified_at;
  if (!aptIso) return false;
  const aptTs = new Date(aptIso).getTime();
  if (!Number.isFinite(aptTs)) return false;
  const clientIso = client.primary_eligibility_checked
    || client.last_eligibility_check
    || client.primary_eligibility_checked_at;
  if (!clientIso) return true;  // No client-level check → snapshot wins.
  const clientTs = new Date(clientIso).getTime();
  if (!Number.isFinite(clientTs)) return true;
  return aptTs >= clientTs;
}

/**
 * `<EligibilityBadge>` — drop-in replacement for every hand-rolled
 * eligibility pill.  Inline, compact, icon + label + optional
 * tooltip.  Used in:
 *   - Client chart header (size="md")
 *   - Client management list cells (size="sm")
 *   - Eligibility tab table rows (size="sm")
 *   - Calendar / appointment cards (size="xs")
 *
 * Feb 2026 — accepts an optional ``onReverify`` callback.  When the
 * pill represents a stale-for-DOS verdict (``stale_unknown`` or a
 * stale ``inactive``), passing ``onReverify`` makes the pill a button
 * so the biller can fire a one-click re-verify for the specific DOS
 * without leaving the surface.  Pure-display callers (where there's
 * no actionable DOS context) just omit the prop and get the original
 * static behaviour.
 */
export function EligibilityBadge({
  status,
  label,
  tooltip,
  size = 'sm',
  className = '',
  showLabel = true,
  onReverify,
  reverifyAriaLabel,
  'data-testid': dataTestId,
}) {
  const d = getEligibilityDisplay(status);
  const Icon = d.IconComponent;
  const sizeMap = {
    xs: { pad: 'px-1.5 py-0.5', text: 'text-[10px]', icon: 10 },
    sm: { pad: 'px-2 py-0.5', text: 'text-xs', icon: 12 },
    md: { pad: 'px-2.5 py-1', text: 'text-sm', icon: 14 },
    lg: { pad: 'px-3 py-1.5', text: 'text-base', icon: 16 },
  };
  const s = sizeMap[size] || sizeMap.sm;
  const baseClass = `inline-flex items-center gap-1 rounded-full border font-medium ${d.bg} ${d.color} ${d.border} ${s.pad} ${s.text} ${className}`.trim();
  const inner = (
    <>
      <Icon className={d.iconClass} size={s.icon} />
      {showLabel && <span>{label || d.label}</span>}
    </>
  );
  if (typeof onReverify === 'function') {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReverify(); }}
        className={`${baseClass} cursor-pointer hover:brightness-95 active:scale-95 transition-transform`}
        title={tooltip || 'Click to re-verify for DOS'}
        aria-label={reverifyAriaLabel || 'Re-verify eligibility for date of service'}
        data-testid={dataTestId}
      >
        {inner}
      </button>
    );
  }
  return (
    <span
      className={baseClass}
      title={tooltip || undefined}
      data-testid={dataTestId}
    >
      {inner}
    </span>
  );
}

/**
 * `getAppointmentEligibilityIndicator` — single source of truth for the
 * tiny eligibility icon on calendar appointment cards (month/week/day).
 *
 * Jun 2026 (user requirement): PAST appointments must show the status +
 * insurance from the check that was performed FOR that DOS (the
 * appointment-level snapshot stamped by eligibility propagation /
 * manual verify / the DOS backfill) — NEVER the client's current
 * eligibility status or current chart insurance.  No snapshot for that
 * DOS ⇒ grey "not checked" dot.  Today/future keeps the live logic.
 *
 * Returns null (render nothing) or { label, color, title }.
 */
export function getAppointmentEligibilityIndicator(apt, client) {
  if (!apt?.client_id || !client) return null;
  if (!['client_session', 'group'].includes(apt.appointment_type)) return null;
  const selfPay = isClientSelfPay(client);
  const dos = String(apt.start_time || '').substring(0, 10);
  const now = new Date();
  const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isPast = dos && dos < todayLocal;

  if (isPast && !selfPay) {
    const snapStatus = apt.eligibility_status;
    if (!snapStatus || !apt.eligibility_verified_at) {
      return {
        label: '○',
        color: 'text-gray-400',
        title: `Not checked for DOS ${dos}\nRun an eligibility check for this date to update it.`,
      };
    }
    const d = getEligibilityDisplay(snapStatus);
    const checkedOn = new Date(apt.eligibility_verified_at).toLocaleDateString();
    const lines = [
      `${d.label} — as checked for DOS ${dos}`,
      `Checked on: ${checkedOn}${apt.eligibility_is_manual ? ' (manual verification)' : ''}`,
    ];
    if (apt.eligibility_insurance) lines.push(`Insurance at check: ${apt.eligibility_insurance}`);
    const glyph = (snapStatus === 'verified' || snapStatus === 'active') ? '✓'
      : (['inactive', 'rejected', 'failed'].includes(snapStatus) ? '✗' : '○');
    return {
      label: apt.eligibility_is_retroactive ? `${glyph}ᴿ` : glyph,
      color: d.color,
      title: lines.join('\n'),
    };
  }

  // Today / future — live logic (unchanged behavior).
  const status = client.primary_eligibility_status;
  if (!selfPay && (!status || status === 'not_checked')) return null;
  const aptVerified = !selfPay && apt.eligibility_verified && isAppointmentSnapshotFresh(apt, client);
  const aptRetro = !selfPay && apt.eligibility_is_retroactive;
  const { icon, color } = aptVerified
    ? { icon: '✓', color: 'text-green-700' }
    : (getDOSEligibilityDisplay(client, apt.start_time) || { icon: null, color: '' });
  let title = eligibilityTooltip(client);
  if (aptVerified && apt.eligibility_verified_at) {
    const d = new Date(apt.eligibility_verified_at).toLocaleDateString();
    title = aptRetro
      ? `Eligible (Retroactive)\nVerified on: ${d}\nCovers DOS: ${dos}\n${title}`
      : `Eligible\nVerified on: ${d}\n${title}`;
  }
  return { label: aptRetro ? `${icon}ᴿ` : icon, color, title };
}

/**
 * `eligibilityTooltip` — produces the multi-line hover tooltip used
 * across the entire app (client cards, chart header, calendar dots,
 * client list).  Single source of truth so V2 + legacy show identical
 * text.  Copied verbatim from the inline helper that lived in App.js
 * so legacy callers can switch over without behaviour change.
 *
 * Accepts an optional `checkDetail` object (an item from the
 * eligibility-history list) — fields on it override the corresponding
 * client-level fields, so eg. the EligibilityHistory tab can show the
 * tooltip for a particular check.
 *
 *   <Label>
 *   Checked for DOS: <mm/dd/yyyy or "General (no DOS)">
 *   Checked on: <mm/dd/yyyy hh:mm>
 *   Coverage: <eff> → <end>
 */
export function eligibilityTooltip(client, checkDetail = null) {
  if (!client) return '';

  // Self-pay short-circuit — there's nothing to verify, surface the
  // same single-line label EligibilityIndicator does.
  // (Caller passes us the client, so we can resolve self-pay locally.)
  // We avoid an `isClientSelfPay` import dep cycle by doing the check
  // inline — `insurances` is the canonical schema field.
  const activeIns = (client.insurances || []).filter((i) => i && i.status === 'active');
  const selfPay = activeIns.length === 0 && !client.primary_insurance;

  const status = selfPay ? 'self_pay' : (client.primary_eligibility_status || 'not_checked');
  const { label } = getEligibilityDisplay(status);

  if (selfPay) return label;

  const lastChecked = client.primary_eligibility_checked || client.last_eligibility_check;
  // Staleness rule (mirrors legacy `EligibilityIndicator`): a verified
  // check ages out after 24h.  Surface this in the tooltip so users
  // know to re-run eligibility instead of trusting the green check.
  let hoursAgo = null;
  let isStale = true;
  if (lastChecked) {
    hoursAgo = Math.round((Date.now() - new Date(lastChecked).getTime()) / 3600000);
    isStale = hoursAgo >= 24;
  }
  const isVerifiedLike = status === 'verified' || status === 'active';
  const headerLabel = (isVerifiedLike && isStale && lastChecked) ? `${label} (Stale)` : label;

  const lines = [headerLabel];

  // mm/dd/yyyy HH:MM (local) when the string carries a time, else mm/dd/yyyy.
  const fmt = (raw, { timeIfPresent = true } = {}) => {
    if (!raw) return null;
    const s = String(raw);
    const hasTime = s.includes('T') || s.includes(':');
    try {
      const d = new Date(hasTime ? s : s + 'T00:00:00');
      if (Number.isNaN(d.getTime())) return null;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      if (!timeIfPresent || !hasTime) return `${mm}/${dd}/${yyyy}`;
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${mm}/${dd}/${yyyy} ${hh}:${mi}`;
    } catch {
      return null;
    }
  };

  const dos = checkDetail?.date_of_service || client.last_eligibility_dos;
  lines.push(`Checked for DOS: ${dos ? fmt(dos, { timeIfPresent: false }) || dos : 'General (no DOS)'}`);

  const checkedAt = checkDetail?.checked_at
    || checkDetail?.response_date
    || client.primary_eligibility_checked
    || client.last_eligibility_check;
  const checkedStr = fmt(checkedAt);
  if (checkedStr) {
    // Tack on a human-readable age so users can tell at a glance
    // "this was checked 2 hours ago" vs "this was checked 4 days
    // ago".  Only when a checkDetail isn't overriding — keep the
    // detail view focused on that specific check.
    const ageHint = (!checkDetail && hoursAgo !== null)
      ? (hoursAgo < 1 ? ' (just now)'
        : hoursAgo < 24 ? ` (${hoursAgo}h ago)`
        : ` (${Math.floor(hoursAgo / 24)}d ago)`)
      : '';
    lines.push(`Checked on: ${checkedStr}${ageHint}`);
  }

  const eff = checkDetail?.coverage_effective_date || client.primary_effective_date;
  const end = checkDetail?.coverage_termination_date || client.primary_end_date;
  if (eff) {
    const effStr = fmt(eff, { timeIfPresent: false }) || eff;
    const endStr = end ? (fmt(end, { timeIfPresent: false }) || end) : null;
    lines.push(`Coverage: ${effStr}${endStr ? ` → ${endStr}` : ''}`);
  }

  return lines.join('\n');
}
